import { NgClass } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild, input, output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

import { CurrencyPipe, CurrencySymbolPipe, DeviceService } from '@frontend/vanilla/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AppConstants, Config } from '../../../../config/appConstants';
import { DataGrid } from '../../../../model/common.model';
import { DepositInfo } from '../../../../model/depositInfo.model';
import { QDInstrument, TxnLimitInfo } from '../../../../model/qdInstrument.model';
import { BehaviorDataService } from '../../../../service/behaviorData.service';
import { BonusService } from '../../../../service/bonus.service';
import { DepositService } from '../../../../service/deposit.service';
import { GtmService } from '../../../../service/gtm.service';
import { OptionListService } from '../../../../service/optionList.service';
import { UserDataService } from '../../../../service/userDataService';
import { ValidnumberDirective } from '../../../../shared/directives/validnumber.directive';
import { providerBalanceValidator } from '../../../../shared/validators/providerbalance.validator';
import { selfSetLimit } from '../../../../shared/validators/selfsetlimit.validator';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'gvc-amount-grid',
    templateUrl: 'amount-grid.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        ValidnumberDirective,
        NgClass,
        MatError,
        TranslateModule,
        CurrencySymbolPipe,
        CurrencyPipe,
    ],
})
export class GVCAmountGridComponent implements OnInit, OnChanges, OnDestroy {
    @Input() gvcAmountFormGroup: FormGroup;
    @Input() selectedOption: QDInstrument;
    gridData = input.required<DataGrid>();
    showKeyboard = output<any>();
    subscriptions$: Subscription[] = [];

    ccFamily = AppConstants.CCFAMILY;
    depositInfo: DepositInfo;
    @ViewChild('userAmount') userAmount: ElementRef;
    minLimit: number;
    maxLimit: number;
    amountInAccCurrency: any;
    ccDepositLimitCheckEnabled: boolean;
    ccCurrentDepositLimit: string;
    showAmountError: boolean;
    isKeyboardOpen: boolean = false;
    isMobile: boolean = false;
    selfLimits: TxnLimitInfo;
    maxDepositLimit: string;

    constructor(
        private depositService: DepositService,
        private behaviorData: BehaviorDataService,
        public optionListService: OptionListService,
        public elementRef: ElementRef,
        private GTMService: GtmService,
        private userDataService: UserDataService,
        private bonusService: BonusService,
        private deviceService: DeviceService,
    ) {
        this.depositInfo = this.depositService?.depositInfo;
    }
    ngOnChanges() {
        this.initialize();
    }

    ngOnInit() {
        this.getBehaviorData();
    }

    initialize() {
        this.maxDepositLimit = '0';
        this.showAmountError = true;
        this.isMobile = this.deviceService.isMobile;
        const configParams = this.userDataService?.userData?.configParams;
        this.ccDepositLimitCheckEnabled = configParams?.isCreditCardDepositLimitCheckEnabled === Config.YES;
        if (this.ccDepositLimitCheckEnabled) {
            this.ccCurrentDepositLimit = configParams?.currentCreditCardDepositLimit;
        }
        this.depositInfo.amountInfo.nativeCurrency =
            this.selectedOption?.currencyInfo?.defaultCurr || this.selectedOption?.currencyInfo?.currencyList[0];
        this.selfLimits = this.selectedOption?.paymentMethodInfo?.txnLimitInfo;
        this.gvcAmountFormGroup.addControl('userAmount', new FormControl(''));
        const maxLimit = this.selectedOption?.paymentMethodInfo?.txnLimitInfo?.maxLimit?.value;
        if (maxLimit) {
            this.maxDepositLimit = parseFloat(maxLimit).toFixed(2);
        }
        if (this.gridData() && this.gridData()?.nativeAmountString) {
            this.depositInfo.amountInfo.nativeAmountString = this.gridData()?.nativeAmountString;
            this.validateAmount();
        }
    }

    onChangeAmt() {
        this.validateAmount();
        this.show();
        this.GTMService.raiseOnChangeAmtGTMEvent('Deposit Amount - Select', 'Typed', this.depositInfo?.amountInfo?.nativeAmountString);
        this.behaviorData.changeEnteredAmount(this.depositInfo?.amountInfo?.nativeAmountString);
    }
    //event handler for amount validations
    validateAmount() {
        this.showAmountError = true;
        this.minLimit = this.selectedOption?.paymentMethodInfo?.minDepositLimit?.value;
        this.maxLimit = this.selectedOption?.paymentMethodInfo?.maxDepositLimit?.value;
        this.setValidator(this.minLimit, this.maxLimit);

        this.amountInAccCurrency = this.depositService.getAmountInAccountCurrency();
        if (this.selectedOption?.accountBalance && parseFloat(this.amountInAccCurrency) > this.selectedOption?.accountBalance) {
            this.gvcAmountFormGroup?.controls?.['userAmount']?.setValidators(
                Validators.compose([
                    Validators.required,
                    Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
                    Validators.min(this.minLimit),
                    Validators.max(this.maxLimit),
                    providerBalanceValidator(true),
                ]),
            );
        }

        const txnCurrency = this.depositInfo?.amountInfo?.nativeCurrency;
        const accCurrency = this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode;

        if (txnCurrency?.toUpperCase() !== accCurrency?.toUpperCase()) {
            const convertedValue = Number(this.depositService.getAmountInTxnCurrency());
            if (this.amountInAccCurrency < this.maxLimit) {
                this.setValidator(this.minLimit, convertedValue);
            }
            if (this.amountInAccCurrency < this.minLimit) {
                const min = convertedValue < this.minLimit ? this.minLimit : convertedValue;
                this.setValidator(min, this.maxLimit);
            }
        }

        this.gvcAmountFormGroup?.controls['userAmount']?.markAsTouched();
        this.gvcAmountFormGroup?.controls['userAmount']?.updateValueAndValidity();
        this.validateSelfSetLimits();
    }

    setValidator(min: number, maxLimit: number) {
        this.gvcAmountFormGroup?.controls?.['userAmount']?.setValidators(
            Validators.compose([Validators.required, Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'), Validators.min(min), Validators.max(maxLimit)]),
        );
    }

    validateSelfSetLimits() {
        this.behaviorData.changeshowCCDepositLimitCheckMsg(false);
        if (this.selfLimits?.type?.includes('purchase_limit_')) {
            if (
                +this.depositInfo?.amountInfo?.nativeAmountString > +this.selectedOption?.paymentMethodInfo?.minDepositLimit?.value &&
                +this.depositInfo?.amountInfo?.nativeAmountString > +this.maxDepositLimit
            ) {
                this.selfLimitValidation();
                this.showAmountError = false;
            } else {
                this.showAmountError = true;
            }
        }
        if (this.ccDepositLimitCheckEnabled && this.selectedOption?.creditCard) {
            if (
                parseFloat(this.ccCurrentDepositLimit) < parseFloat(this.amountInAccCurrency) ||
                parseFloat(this.ccCurrentDepositLimit) < parseFloat(this.selectedOption?.paymentMethodInfo?.txnLimitInfo?.minLimit?.value)
            ) {
                this.showAmountError = false;
                this.behaviorData.changeshowCCDepositLimitCheckMsg(true);
                this.gvcAmountFormGroup?.controls?.['userAmount']?.setValidators(
                    Validators.compose([
                        Validators.required,
                        Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
                        Validators.min(this.minLimit),
                        Validators.max(this.maxLimit),
                        providerBalanceValidator(true),
                        selfSetLimit(true),
                    ]),
                );
            }
            this.gvcAmountFormGroup?.controls['userAmount']?.markAsTouched();
            this.gvcAmountFormGroup?.controls['userAmount']?.updateValueAndValidity();
            this.depositService.resizeCashier();
        }
    }
    selfLimitValidation() {
        this.gvcAmountFormGroup?.controls?.['userAmount']?.setValidators(
            Validators.compose([
                Validators.required,
                Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
                Validators.min(this.minLimit),
                Validators.max(parseFloat(this.maxDepositLimit)),
                selfSetLimit(true),
            ]),
        );
        this.gvcAmountFormGroup?.controls?.['userAmount']?.updateValueAndValidity();
    }

    show() {
        this.isKeyboardOpen = true;
        this.emitKeyboardInfo();
    }
    hide() {
        const amount = this.depositInfo?.amountInfo?.nativeAmountString;
        this.depositInfo.amountInfo.nativeAmountString = amount ? (!isNaN(amount) ? parseFloat(amount).toFixed(2) : amount) : '';
        this.validateAmount();
        this.bonusService.handleBonusAmount(this.depositInfo);
        this.isKeyboardOpen = false;
        this.emitKeyboardInfo();
    }
    emitKeyboardInfo() {
        setTimeout(() => {
            this.showKeyboard.emit({
                isVisible: this.isKeyboardOpen,
                currentValue: this.depositInfo?.amountInfo?.nativeAmountString,
                name: 'amount',
            });
            this.depositService.resizeCashier();
        }, 0);
    }

    getBehaviorData() {
        this.subscriptions$.push(
            this.behaviorData.getEnteredAmount.subscribe((data: any) => {
                this.depositInfo.amountInfo.nativeAmountString = data;
                if (data) {
                    this.emitKeyboardInfo();
                    this.validateAmount();
                }
            }),
        );
    }

    keyPressNumbersDecimal(event: any) {
        return this.depositService.keyPressNumbersDecimal(event);
    }

    ngOnDestroy(): void {
        this.subscriptions$.map((subscription) => subscription.unsubscribe());
    }
}
